// /**
//  * adapted from http://stemkoski.github.io/Three.js/Sprite-Text-Labels.html
//  */

import * as THREE from 'three';

type Color = { r: number; g: number; b: number; a: number };

function createDepthFadeSpriteMaterial(texture: THREE.Texture): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      map: { value: texture },
      uDepthTexture: { value: null },
      uNear: { value: 0.1 },
      uFar: { value: 10000 },
      uFadeDistance: { value: 2.0 },
      uFadeStart: { value: 1.0 },
      uDepthFadeEnabled: { value: false },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uRotation: { value: 0 },
    },
    vertexShader: `
      uniform float uRotation;
      varying vec2 vUv;

      void main() {
        vUv = uv;

        // Billboard effect: always face the camera
        // Use modelViewMatrix directly to avoid precision loss with large world coordinates
        // The modelViewMatrix combines model and view transforms in 64-bit JS before GPU upload
        vec4 viewPosition = modelViewMatrix * vec4(0.0, 0.0, 0.0, 1.0);

        // Extract scale from modelMatrix for proper sizing
        vec3 scale = vec3(
          length(modelMatrix[0].xyz),
          length(modelMatrix[1].xyz),
          length(modelMatrix[2].xyz)
        );

        // Apply local vertex position scaled by model scale, in view space (camera-aligned),
        // turned in the screen plane by uRotation (0 = upright).
        vec3 scaled = position * scale;
        float c = cos(uRotation);
        float s = sin(uRotation);
        viewPosition.xy += vec2(c * scaled.x - s * scaled.y, s * scaled.x + c * scaled.y);
        viewPosition.z += scaled.z;

        gl_Position = projectionMatrix * viewPosition;
      }
    `,
    fragmentShader: `
      uniform sampler2D map;
      uniform sampler2D uDepthTexture;
      uniform float uNear;
      uniform float uFar;
      uniform float uFadeDistance;
      uniform float uFadeStart;
      uniform bool uDepthFadeEnabled;
      uniform vec2 uResolution;

      varying vec2 vUv;

      void main() {
        vec4 texColor = texture2D(map, vUv);
        if (texColor.a < 0.01) discard;

        float fadedAlpha = texColor.a;

        if (uDepthFadeEnabled) {
          vec2 screenUV = gl_FragCoord.xy / uResolution;
          float pcDepth = texture2D(uDepthTexture, screenUV).r;

          // Only apply fade if there's valid point cloud depth
          if (pcDepth > 0.0 && pcDepth < 1.0) {
            // Linearize depths for proper distance comparison
            float zNdc = pcDepth * 2.0 - 1.0;
            float pcLinearDepth = (2.0 * uNear * uFar) / (uFar + uNear - zNdc * (uFar - uNear));

            float fragNdc = gl_FragCoord.z * 2.0 - 1.0;
            float fragLinearDepth = (2.0 * uNear * uFar) / (uFar + uNear - fragNdc * (uFar - uNear));

            // How far behind the point cloud is this fragment?
            float depthDiff = fragLinearDepth - pcLinearDepth;

            // Fade based on depthDiff
            if (depthDiff > uFadeStart) {
              float fadeAmount = clamp(depthDiff / uFadeDistance, 0.0, 1.0);
              fadedAlpha = texColor.a * (1.0-fadeAmount);
            }
          }
        }

        if (fadedAlpha < 0.001) discard;
        gl_FragColor = vec4(texColor.rgb, fadedAlpha);
      }
    `,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
}

export class TextSprite extends THREE.Object3D {
  texture: THREE.Texture;
  material: THREE.ShaderMaterial;
  sprite: THREE.Sprite;
  borderThickness: number;
  fontface: string;
  fontsize: number;
  borderColor: Color;
  backgroundColor: Color;
  textColor: Color;
  text: string;
  constructor(text = '') {
    super();

    const texture = new THREE.Texture();
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    this.texture = texture;

    // Use depth fade shader material
    this.material = createDepthFadeSpriteMaterial(texture);

    // Create a plane geometry for the sprite (Sprite doesn't work well with ShaderMaterial)
    const geometry = new THREE.PlaneGeometry(1, 1);
    this.sprite = new THREE.Mesh(geometry, this.material) as unknown as THREE.Sprite;
    this.add(this.sprite);

    this.borderThickness = 4;
    this.fontface = 'Arial';
    this.fontsize = 28;
    this.borderColor = { r: 0, g: 0, b: 0, a: 1.0 };
    this.backgroundColor = { r: 255, g: 255, b: 255, a: 1.0 };
    this.textColor = { r: 255, g: 255, b: 255, a: 1.0 };
    this.visible = true;
    this.text = text;
    // this.position.set(0, 0, 0);
    this.update();
  }

  setText(text: string) {
    if (this.text !== text) {
      this.text = text;

      this.update();
    }
  }

  setTextColor(color: Color) {
    this.textColor = color;

    this.update();
  }

  setBorderColor(color: Color) {
    this.borderColor = color;

    this.update();
  }

  setBackgroundColor(color: Color) {
    this.backgroundColor = color;

    this.update();
  }

  setVisible(visible: boolean) {
    if (this.visible !== visible) {
      this.visible = visible;
      this.update();
    }
  }

  setFontsize(fontsize: number) {
    this.fontsize = fontsize;
    this.update();
  }

  /** Turn the text in the screen plane (radians, counter-clockwise; 0 = upright). */
  setScreenRotation(radians: number) {
    this.material.uniforms.uRotation.value = radians;
  }

  setScale(scale: number) {
    this.scale.set(scale, scale, scale);
    this.update();
  }

  update() {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;
    context.font = 'Bold ' + this.fontsize + 'px ' + this.fontface;

    // get size data — supports multi-line text (split on '\n'). Width is the
    // widest line; height grows with the number of lines.
    const lines = this.text.split('\n');
    const lineHeight = this.fontsize * 1.4;
    let textWidth = 0;
    for (const line of lines) {
      textWidth = Math.max(textWidth, context.measureText(line).width);
    }
    const textBlockHeight = lineHeight * lines.length;
    const margin = 5;
    const spriteWidth = 2 * margin + textWidth + 2 * this.borderThickness;
    const spriteHeight = textBlockHeight + 2 * this.borderThickness;

    context.canvas.width = spriteWidth;
    context.canvas.height = spriteHeight;
    context.font = 'Bold ' + this.fontsize + 'px ' + this.fontface;

    // background color
    context.fillStyle = 'rgba(' + this.backgroundColor.r + ',' + this.backgroundColor.g + ',' + this.backgroundColor.b + ',' + this.backgroundColor.a + ')';
    // border color
    context.strokeStyle = 'rgba(' + this.borderColor.r + ',' + this.borderColor.g + ',' + this.borderColor.b + ',' + this.borderColor.a + ')';

    context.lineWidth = this.borderThickness;
    this.roundRect(context, this.borderThickness / 2, this.borderThickness / 2, textWidth + this.borderThickness + 2 * margin, textBlockHeight + this.borderThickness, 6);

    // text — stroke (outline) then fill each line at its baseline
    context.strokeStyle = 'rgba(0, 0, 0, 1.0)';
    context.fillStyle = 'rgba(' + this.textColor.r + ',' + this.textColor.g + ',' + this.textColor.b + ',' + this.textColor.a + ')';
    for (let i = 0; i < lines.length; i++) {
      const baselineY = this.fontsize + this.borderThickness + i * lineHeight;
      context.strokeText(lines[i], this.borderThickness + margin, baselineY);
      context.fillText(lines[i], this.borderThickness + margin, baselineY);
    }

    // Dispose old texture before creating new one to prevent memory leak
    if (this.texture) {
      this.texture.dispose();
    }

    const texture = new THREE.Texture(canvas);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.needsUpdate = true;

    // Update the shader material's map uniform
    this.material.uniforms.map.value = texture;
    this.texture = texture;

    this.sprite.scale.set(spriteWidth * 0.01, spriteHeight * 0.01, 1.0);
  }

  roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  dispose() {
    if (this.texture) {
      this.texture.dispose();
    }
    if (this.material) {
      this.material.dispose();
    }
  }
}
